import React, { useMemo, useRef } from 'react';
import { ButtonBase, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import {
  CategoryRounded,
  CleaningServicesRounded,
  Inventory2Rounded,
  LocalDrinkRounded,
  LocalGroceryStoreRounded,
  LunchDiningRounded,
  SmokingRoomsRounded,
  StarRounded,
} from '@mui/icons-material';

function normalizeText(value) {
  return String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function iconForCategory(name) {
  const text = normalizeText(name);
  if (/(bebida|gaseosa|agua|jugo|cerveza|refresco)/.test(text)) return LocalDrinkRounded;
  if (/(snack|confite|dulce|galleta)/.test(text)) return LunchDiningRounded;
  if (/(lacteo|leche|queso|yogur)/.test(text)) return LocalGroceryStoreRounded;
  if (/(aseo|limpieza)/.test(text)) return CleaningServicesRounded;
  if (/(cigarrillo|tabaco)/.test(text)) return SmokingRoomsRounded;
  if (/(general|abarrote)/.test(text)) return Inventory2Rounded;
  return CategoryRounded;
}

export default function CategoryBar({ categories = [], selected, onSelect, loading = false }) {
  const sliderRef = useRef(null);

  const rows = Array.isArray(categories)
    ? categories.filter((row) => String(row?.id ?? row?.categoria_id ?? '').trim())
    : [];

  const navigationRows = useMemo(() => [
    { id: '', nombre: 'Destacados', destacado: true },
    ...rows,
  ], [rows]);

  const handleWheel = (event) => {
    const node = sliderRef.current;
    if (!node || node.scrollWidth <= node.clientWidth) return;
    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      event.preventDefault();
      node.scrollLeft += event.deltaY;
    }
  };

  return (
    <Stack
      ref={sliderRef}
      direction="row"
      spacing={0.7}
      onWheel={handleWheel}
      sx={{
        width: '100%',
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        pb: 0.45,
        scrollBehavior: 'smooth',
        scrollSnapType: 'x proximity',
        overscrollBehaviorX: 'contain',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'thin',
        scrollbarColor: '#a9bdd4 transparent',
        '&::-webkit-scrollbar': { height: 5 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#a9bdd4', borderRadius: 999 },
        '&::-webkit-scrollbar-thumb:hover': { bgcolor: '#7e9fbe' },
      }}
    >
      {loading && !rows.length ? (
        <Paper
          elevation={0}
          sx={{
            minWidth: 116,
            height: 44,
            border: '1px solid #dce5ef',
            borderRadius: 999,
            display: 'grid',
            placeItems: 'center',
            flex: '0 0 auto',
            bgcolor: '#fff',
          }}
        >
          <CircularProgress size={20} />
        </Paper>
      ) : navigationRows.map((category) => {
        const id = String(category?.id ?? category?.categoria_id ?? '').trim();
        const label = category?.nombre || category?.name || 'Categoría';
        const Icon = category?.destacado ? StarRounded : iconForCategory(label);
        const active = String(selected || '') === id;

        return (
          <Paper
            key={category?.destacado ? 'destacados' : id}
            elevation={0}
            sx={{
              height: 44,
              minWidth: 'max-content',
              border: '1px solid',
              borderColor: active ? '#1772e8' : '#d7e1ec',
              bgcolor: active ? '#1772e8' : '#fff',
              color: active ? '#fff' : '#2d425b',
              overflow: 'hidden',
              borderRadius: 999,
              boxShadow: active ? '0 6px 14px rgba(23,114,232,.17)' : '0 2px 8px rgba(31,64,99,.035)',
              flex: '0 0 auto',
              scrollSnapAlign: 'start',
              '@media (max-height: 820px)': { height: 40 },
            }}
          >
            <ButtonBase
              onClick={() => onSelect?.(category?.destacado ? null : category)}
              sx={{
                height: '100%',
                px: 1.45,
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: active ? 'rgba(255,255,255,.08)' : '#f6f9fc' },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.65}>
                <Icon sx={{ fontSize: 19, flexShrink: 0 }} />
                <Typography variant="body2" fontWeight={900} noWrap>{label}</Typography>
              </Stack>
            </ButtonBase>
          </Paper>
        );
      })}
    </Stack>
  );
}
